import { useCallback, useEffect, useRef, useState } from "react";

import { GRID_GAP, ROW_HEIGHT } from "./grid.constants";
import { nearestSize, spanForSize } from "./grid.utils";
import type {
  IGridMetrics,
  IWidgetDefinition,
  TResizeAxis,
  TWidgetSizeKey,
} from "./types";

interface IResizeSession {
  instanceId: string;
  /** The size the pointer is currently snapped to. */
  previewSize: TWidgetSizeKey;
  axis: TResizeAxis;
}

interface IUseWidgetResizeArgs {
  metrics: IGridMetrics;
  /** Called once, on release, with the size the pointer settled on. */
  onCommit: (instanceId: string, size: TWidgetSizeKey) => void;
}

/**
 * Pointer-driven resize that snaps to the widget's allowed sizes.
 *
 * The pointer moves continuously but a widget only has a handful of legal
 * footprints, so the drag reports the *nearest legal size* the whole way. The
 * caller applies that size live — the card actually grows and the grid reflows —
 * and the layout is written exactly once, on release, so a jittery gesture
 * cannot turn into a stream of saves.
 *
 * `axis` comes from which handle was grabbed. An edge handle that ignored the
 * other axis' movement would still snap on it, which is the classic "I dragged
 * right and it got taller" bug.
 */
export function useWidgetResize({ metrics, onCommit }: IUseWidgetResizeArgs) {
  const [session, setSession] = useState<IResizeSession | null>(null);

  const dragRef = useRef<{
    instanceId: string;
    definition: IWidgetDefinition;
    axis: TResizeAxis;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    pointerId: number;
    element: HTMLElement;
    latestSize: TWidgetSizeKey;
  } | null>(null);

  const endDrag = useCallback(
    (shouldCommit: boolean) => {
      const drag = dragRef.current;
      dragRef.current = null;
      setSession(null);
      if (!drag) return;

      try {
        drag.element.releasePointerCapture(drag.pointerId);
      } catch {
        // The pointer can already be released (element unmounted mid-drag).
      }
      if (shouldCommit) onCommit(drag.instanceId, drag.latestSize);
    },
    [onCommit],
  );

  const start = useCallback(
    (
      event: React.PointerEvent<HTMLElement>,
      instanceId: string,
      definition: IWidgetDefinition,
      currentSize: TWidgetSizeKey,
      axis: TResizeAxis,
    ) => {
      // Left button / primary touch only — a right-click must not start a drag.
      if (event.button !== 0) return;
      if (metrics.columns <= 0) return;

      event.preventDefault();
      event.stopPropagation();

      const element = event.currentTarget;
      const span = spanForSize(currentSize, metrics.columns);

      element.setPointerCapture(event.pointerId);
      dragRef.current = {
        instanceId,
        definition,
        axis,
        startX: event.clientX,
        startY: event.clientY,
        startW: span.w,
        startH: span.h,
        pointerId: event.pointerId,
        element,
        latestSize: currentSize,
      };
      setSession({ instanceId, previewSize: currentSize, axis });
    },
    [metrics.columns],
  );

  const isGestureActive = session !== null;

  useEffect(() => {
    if (!isGestureActive) return;

    const handleMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;

      const stepX = metrics.cellWidth + GRID_GAP;
      const stepY = ROW_HEIGHT + GRID_GAP;
      if (stepX <= 0) return;

      const deltaW =
        drag.axis === "y"
          ? 0
          : Math.round((event.clientX - drag.startX) / stepX);
      const deltaH =
        drag.axis === "x"
          ? 0
          : Math.round((event.clientY - drag.startY) / stepY);

      const snapped = nearestSize(
        drag.definition,
        { w: drag.startW + deltaW, h: drag.startH + deltaH },
        metrics.columns,
      );

      if (snapped !== drag.latestSize) {
        drag.latestSize = snapped;
        setSession({
          instanceId: drag.instanceId,
          previewSize: snapped,
          axis: drag.axis,
        });
      }
    };

    const handleUp = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (drag && event.pointerId !== drag.pointerId) return;
      endDrag(true);
    };

    // Escape abandons the gesture without applying it — the standard out for a
    // direct-manipulation drag the user started by mistake.
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") endDrag(false);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
      window.removeEventListener("keydown", handleKey);
    };
    // Keyed on whether a gesture is running, not on the session object: the
    // session changes identity on every snap, which would tear down and re-bind
    // four window listeners mid-drag for no reason.
  }, [isGestureActive, metrics.cellWidth, metrics.columns, endDrag]);

  return { session, start };
}
